// === ZAGX v2 — Main Entry Point ===
// Wires all 24 modules together, sets shared state refs,
// registers callbacks, exposes onclick globals, runs app init.

// ─── Error Monitoring (must init before anything else) ───
import { stripSecrets, hasSecrets } from './core/secrets.js';
import { initSentry, captureError, setSentryUser, addBreadcrumb } from './core/sentry.js';
initSentry();

// ─── Core Imports ───
import { _setStateRef, esc, uid, f, fU, fT, fP, pc, now, fByCur, toUSD, ds, ml, VALID_CURRENCIES } from './core/utils.js';
import { _setMathState, xirr, getMs, mTRY, hMoTRY, hTotTRY, hTotUSD, hVal, hPnl, tdAccrued } from './core/math.js';
import { S, stampItem, resolveConflict, _setPersistenceFunctions, _setRenderCallbacks, save, _sanitizeImportArray, _IMPORT_KEYS, _getSaveTimer, _clearSaveTimer, flushCloudNow } from './core/state.js';
import { _setOnLoginSuccess, _setOnOnline, sbInit, authLogin, authLogout, authCheckSession, showAuth, hideAuth, updateSyncBadge, _sbHeaders, _sb, _sbUser, _sbReady, _accessToken, _isOnline, _savePending, SB_REST, SUPABASE_ANON_KEY } from './core/auth.js';
import { _setOnLoadComplete, idbSaveState, idbSave, idbList, idbRestore, idbClear, load, cloudSave, cloudLoad, cloudSnapshot, stopCloudPoll, canWriteLocalState, syncableSnapshots } from './core/persistence.js';
import { _setPriceState, _setOnPricesRefreshed, fetchFx, refreshPrices, setupPoll } from './core/prices.js';

// ─── UI Imports ───
import { toast, clMo, fixMobileLayout, setupNavigation, setupModals, setupScrollFades, setupKeyboard, toggleMobileMore, setupToTop } from './ui/layout.js';
import { _safeDestroy, _safeChart, _chartUpdate, renderAllocCh } from './ui/charts.js';

// ─── Neon design-system kit (SPEKTRX redesign port — S0) ───
import SPK from './ui/spk.js';
if (typeof window !== 'undefined') window.SPK = SPK;

// ─── Render Engine ───
import { markDirty, renderAll, renderOnly, _dirty, _pageToSection, _renderMap, setHydrated } from './app/render-loop.js';

// ─── Event Delegation (CSP) ───
import { setupEventDelegation } from './app/event-bindings.js';

// ─── Feature Module Imports ───
import { calcPortfolioXIRR, calcHoldingXIRR, srt, renderP, manP, inlinePrice, openAddH, editH, saveH, closeDailyTd, confirmMaturity, changeTdRate, delH, openBulk, saveBP, switchPortfolio, openPortfolio, savePortfolio, closePortfolio, closeEditingPortfolio, deletePortfolio, renderPortfolioBar, renderPortfolioList, getDefaultPortfolio, getActivePortfolio, getPortfolioHoldings, populatePortfolioSelect, openExitH, clMoExitH, execExitH, exitAvgAll, openAddLot, clMoAddLot, saveAddLot, openLots, clMoLots, delLot } from './modules/portfolio.js';
import { renderM, delMo, populateMonthSelect, openAddMo, calcPlan, saveRow, skipRow, saveMo, reconcileMo, exportMoCsvTemplate, importMoCsv, populateMoPortfolio, moPortChanged } from './modules/monthly.js';
import { notify, scanTradeCrossings, scanMaturities, inlineTPrice, manTP, toggleTdTradeFields, onTdKindChange, calcTdTradePreview, calcExpPnl, renderT, renderTradeJournal, openAddT, editTr, saveT, openExit, execExit, openAddPosition, execAddPosition, thxToggle, thxPreview, trFoldToggle, trQuietToggle } from './modules/trades.js';
import { renderC, renderPnlCh, openCTx, saveCTx, onCashReasonChange, delCashTx, cTxToggleAll, setCashTxClass, reconcileCashTRY, openFxConvert, calcFxConvert, saveFxConvert, rfAction, rfEditTx, rfDeleteTx, renderRF, mnyToggle } from './modules/cash.js';
import { lg, renderLog, setLogTF } from './modules/history.js';
import { initTax, renderTax, taxPickFile, taxImportFile, taxSelectYear, taxToggleWarn, taxMarkSent, taxBookRefund } from './modules/tax.js';
import { initStatements, stmOpen } from './modules/statements.js';
import { renderIntegrity, toggleIntegrityRow } from './modules/integrity-page.js';
import { openLedger, renderLedgerPage } from './modules/alpha-ledger-page.js';
import { toggleDashSection, setTimelineRange, _dashSnapshot, backfillBenchmark, backfillFxRates, setHeatmapMode, renderDash, computeTotalWealthUSD, netGrowthUSD, dashNeedToggle } from './modules/dashboard.js';
import { registerNetWorth, registerCashTRY } from './core/risk-calc.js';
registerNetWorth(computeTotalWealthUSD); // wire canonical net-worth into risk-calc (no import cycle)
registerCashTRY(cashTRYNow); // same, for the TRY balance as of now rather than the last bank reading
import {
  backfillSalaryCashTxs, openExpDef, toggleEdLoan, saveExpDef, deleteExpDef,
  projectMonthlyIncome, openCFMonth,
  cfMoChanged, cfAddIncome, cfRemoveIncome, cfSetIncome, cfIncomeFooter,
  renderCFModal, cfCalcSummary, cfAddItem, cfRemoveItem, cfSetAmt, cfSetField,
  cfPaidByChanged, cfOneOffChanged, cfToggle, saveCFMo, closeCFMonth, cfDeleteMonth, renderCF,
  renderProjection, renderExpDefs, renderCFHist,
  openOTI, saveOTI, deleteOTI, renderOTIList, cashTRYNow
} from './modules/cashflow.js';
import {
  testTdKey, testBistProxy, saveFxRates, saveSett, requestNotifPerm, updateNotifPermUI,
  renderTaxSummary, initSett, loadBackupList, restoreBackup, filterQP, renderQP, saveQP,
  toggleQPPanel, filterQPF, renderQPF, saveQPF, resetAll, exportAll, toggleBistHelp,
  openPBR, confirmBackup, skipBackup, doBackup, importData, exportCSV,
  daysSinceBackup, checkBackup, hideBackupBanner, updateBackupInfo,
  aiProviderChange, saveAISettings, testAI
} from './modules/settings.js';
import { getActiveLoans, runDebtPayoff, dpMoveUp, dpMoveDown } from './modules/debt.js';
import { openBudgetSettings, saveBudgetSettings, renderBudget } from './modules/budget.js';
import { holdingDividends, openDivModal, openDivModalStandalone, calcDvNet, dvTickerLookup, saveDividend, renderDividendList, deleteDividend } from './modules/dividends.js';
import { resetWhatIf, populateWIExpenses, wiCalcProjection, runWhatIf } from './modules/whatif.js';
import { openLiability, saveLiability, deleteLiability, clMoLiab, openLiabPay, execLiabPay, clMoLiabPay, deletePayment, renderLiabilities, totalLiabilitiesUSD, totalLiabilitiesTRY } from './modules/liabilities.js';
import { calcFILevel, checkMilestones, openGoal, saveGoal, deleteGoal, clMoGoal, saveGoalSettings, toggleGoalLink, renderGoals, renderGoalsDashCard } from './modules/goals.js';
import { renderSig, openPasteSignal, execPasteSignal, deleteSignal, expireSignal, saveTickerMapping, updateSignalStatuses, setSignalFilter, signalFilterChange, clearSignalFilters, dismissSignalAlert, dismissAllSignalAlerts, browseSignalFile, openTradeFromSignal, inlineTickerSave, reResolveAllSignals, fetchSignalsFromMongo, enrichSignalsAI, applySignalTP } from './modules/signals.js';
import { renderRisk } from './modules/risk.js';
import { renderAdvisory, advLens, advToggle, advSeeOrig, advDismiss, advAct, advScan, advSelfActed, advWhyDisCancel } from './modules/advisory.js';
import { initAIOrchestrator } from './modules/ai-orchestrator.js';
import { _setLedgerStateRef, initDecisionLedger } from './core/decision-ledger.js';
import { renderJournal, openJournal, jrnFilter, jrnRowToggle } from './modules/trade-journal-page.js';
import { renderBonus, openBonusQuarter, saveBonusQuarter, deleteBonusQuarter, markBonusPaid, switchBonusPlan, openBonusPlan, saveBonusPlan, deleteBonusPlan, allocateBonus, saveAllocation, bonusScanProfile, bonusScanLens, bonusScanAct, bonusRescan, bonusScanAllocate, bonusScanLockAll } from './modules/bonus.js';
import { auditDataIntegrity, reconcileAndRepair } from './core/reconcile.js';

// ─── Search ───
import { openSearch, closeSearch } from './modules/search.js';

// ─── AI Categorization ───
import { openAICat, closeAICat, aiCatToggle, aiCatChange, aiCatChangeEvt, aiCatApproveAll, aiCatSave, aiCatManual, catBadge, getCatStats } from './modules/ai-categorize.js';

// ─── AI Portfolio Insights ───
import { requestAIInsights, renderCachedInsights, dismissAIInsight, clearAIInsights } from './modules/ai-insights.js';

// ─── AI Digest ───
import { generateDigest } from './modules/ai-digest.js';

// ─── AI Trade Journal ───
import { aiReviewTrade, addJournalEntry, saveJournalEntry, cancelJournalEdit, deleteJournalEntry, toggleJournalExpand, getJournalBadge, getJournalExpandRow, offerPostExitReview, refreshExpandedJournals } from './modules/ai-trade-journal.js';

// ─── Push Notifications ───
import { swNotify, getUnreadCount, markNotifRead, markAllNotifsRead, dismissNotif, clearAllNotifs, toggleNotifCenter, closeNotifCenter, setNotifFilter, renderNotifCenter, _updateBellBadge, initPushSettings, savePushSettings, testPushNotif, setupSWMessageListener, setupNotifClickOutside } from './modules/push-notifications.js';

// ─── AI Chat Panel ───
import { openAIChat, closeAIChat, toggleAIChat, sendChatMessage, clearChatHistory, selectQuickAction, aiActionConfirm, aiActionCancel } from './modules/ai-chat.js';

// ─── AI Copilot (P4) ───
import { runCopilotScan, cancelCopilotScan, getCopilotRecommendations, getCopilotAlerts, dismissRecommendation, dismissAlert, getCopilotStatus, isCopilotScanning, clearCopilotData, generateQuickAlerts, evaluateScenarios, createScenario, activatePreset, toggleScenario, deleteScenario, getScenarios, getPresetStatus, SCENARIO_PRESETS, runDeepScan, scanDCAOpportunities } from './modules/ai-copilot.js';
import { calcDrift, generateRebalanceSuggestions, completeReview, activateActions, completeAction, getActiveActions } from './modules/rebalancing.js';

// Rebalance review wizard (exposed on window for the Advisory page's one-click action)
const startRebalanceReview = () => {
  const drift = calcDrift();
  if (!drift) return;
  const suggestions = generateRebalanceSuggestions(drift);
  const body = document.getElementById('rebalWizardBody');
  if (!body) return;

  let html = '';
  // Step 1: Current drift summary
  html += '<div style="margin-bottom:16px"><div style="font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--acc);margin-bottom:8px">Step 1: Current allocation drift</div>';
  html += '<table style="width:100%;font-size:13px;border-collapse:collapse">';
  html += '<tr style="color:var(--t3);font-size:10px"><th style="text-align:left;padding:4px 0">Asset type</th><th style="text-align:right">Current</th><th style="text-align:right">Target</th><th style="text-align:right">Drift</th></tr>';
  drift.drifts.forEach(d => {
    const color = d.status === 'above' ? 'var(--red)' : d.status === 'below' ? 'var(--yel)' : 'var(--grn)';
    html += '<tr style="border-top:1px solid var(--bd)"><td style="padding:5px 0">' + d.category + '</td><td style="text-align:right">' + d.currentPct.toFixed(1) + '%</td><td style="text-align:right;color:var(--t3)">' + d.targetLo + '-' + d.targetHi + '%</td><td style="text-align:right;color:' + color + ';font-weight:600">' + (d.driftPP > 0 ? '+' : '') + d.driftPP.toFixed(1) + 'pp</td></tr>';
  });
  html += '</table></div>';

  // Step 2: Top-level portfolio balance
  if (drift.topLevelAnalysis) {
    html += '<div style="margin-bottom:16px"><div style="font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--acc);margin-bottom:8px">Step 2: Portfolio balance (100-unit model)</div>';
    html += '<div style="padding:8px 12px;background:var(--bg2);border-radius:8px;font-size:13px"><div style="margin-bottom:6px"><span style="color:var(--t3)">Retirement</span> <span style="font-weight:600">$' + Math.round(drift.retTotal).toLocaleString() + '</span> = 100 units</div>';
    Object.values(drift.topLevelAnalysis).forEach(tla => {
      const color = tla.status === 'empty' ? 'var(--red)' : tla.status === 'in_band' ? 'var(--grn)' : 'var(--yel)';
      html += '<div style="margin-bottom:4px"><span style="color:var(--t3)">' + tla.name + '</span> <span style="font-weight:600;color:' + color + '">' + tla.units.toFixed(0) + ' units</span> <span style="color:var(--t3);font-size:11px">($' + Math.round(tla.value).toLocaleString() + ', band: ' + tla.band.lo + '-' + tla.band.hi + ')</span></div>';
    });
    html += '</div></div>';
  }

  // Step 3: DCA suggestions
  html += '<div style="margin-bottom:16px"><div style="font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--acc);margin-bottom:8px">Step 3: DCA rebalancing plan (soft — no selling)</div>';
  if (suggestions.length) {
    suggestions.forEach(s => {
      const icons = { pause: '⏸', boost: '↑', maintain: '→', open: '🆕' };
      const colors = { high: 'var(--red)', medium: 'var(--yel)', low: 'var(--grn)' };
      const bgColors = { pause: 'var(--red-bg)', boost: 'var(--yel-bg)', maintain: 'var(--bg2)', open: 'var(--acc-bg)' };
      html += '<div style="padding:10px 14px;background:' + (bgColors[s.action] || 'var(--bg2)') + ';border-radius:8px;margin-bottom:6px;border-left:3px solid ' + (colors[s.severity] || 'var(--bd)') + '">';
      html += '<div style="font-size:13px;font-weight:600;margin-bottom:3px">' + (icons[s.action] || '') + ' ' + s.message + '</div>';
      if (s.detail) html += '<div style="font-size:12px;color:var(--t2);line-height:1.4">' + s.detail + '</div>';
      html += '</div>';
    });
  } else {
    html += '<div style="padding:10px 14px;background:var(--grn-bg);border-radius:8px;font-size:13px;color:var(--grn)">All allocations within target bands — no adjustments needed.</div>';
  }
  html += '</div>';

  // Step 4: Notes
  html += '<div><div style="font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--acc);margin-bottom:8px">Step 4: Review notes</div>';
  html += '<textarea id="rebalReviewNotes" placeholder="What actions did you take? Any changes to DCA plan?" style="width:100%;height:80px;resize:vertical;font-size:13px;padding:8px 12px;border-radius:8px;border:1px solid var(--bd);background:var(--bg2);color:var(--t1);font-family:inherit"></textarea></div>';

  // Past review comparison
  const reviews = S.rebalancing?.reviews || [];
  if (reviews.length) {
    const last = reviews[reviews.length - 1];
    html += '<div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--bd)"><div style="font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--t3);margin-bottom:6px">Last review: ' + new Date(last.date).toLocaleDateString() + '</div>';
    if (last.notes) html += '<div style="font-size:12px;color:var(--t2);padding:6px 10px;background:var(--bg2);border-radius:6px">' + last.notes + '</div>';
    html += '</div>';
  }

  body.innerHTML = html;
  document.getElementById('moRebal').classList.add('active');
};

const approveRebalanceReview = () => {
  const notes = document.getElementById('rebalReviewNotes')?.value || '';
  // Generate suggestions and convert to trackable actions
  const drift = calcDrift();
  // calcDrift returns null without a usable FX rate (audit 2026-08-01) — do not
  // activate rebalance actions computed from nothing.
  if (!drift) { toast('No usable FX rate — drift cannot be computed, so no actions were activated.', 'a-r', 8000); return; }
  const suggestions = generateRebalanceSuggestions(drift);
  activateActions(suggestions);
  // Complete the review
  completeReview(notes);
  document.getElementById('moRebal').classList.remove('active');
  if (typeof window.save === 'function') window.save();
  toast('Rebalance review completed — ' + getActiveActions().length + ' DCA actions activated', 'a-b');
  if (typeof window.renderAll === 'function') window.renderAll();
};

const showRebalHistory = () => {
  const reviews = S.rebalancing?.reviews || [];
  const body = document.getElementById('rebalWizardBody');
  if (!body) return;
  let html = '<div style="font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--acc);margin-bottom:12px">Review history (' + reviews.length + ' reviews)</div>';
  if (!reviews.length) {
    html += '<div style="font-size:13px;color:var(--t3);padding:12px">No reviews completed yet.</div>';
  } else {
    reviews.slice().reverse().forEach((r, i) => {
      const date = new Date(r.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      html += '<div style="padding:12px;background:var(--bg2);border-radius:8px;margin-bottom:8px">';
      html += '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px"><span style="font-weight:600;font-size:14px">#' + (reviews.length - i) + ' — ' + date + '</span>';
      if (r.emergencies > 0) html += '<span style="font-size:10px;padding:2px 8px;border-radius:10px;background:var(--red-bg);color:var(--red);font-weight:600">' + r.emergencies + ' emergency</span>';
      html += '</div>';
      if (r.driftSnapshot) {
        html += '<table style="width:100%;font-size:12px;border-collapse:collapse;margin-bottom:6px">';
        r.driftSnapshot.forEach(d => {
          const color = d.status === 'above' ? 'var(--red)' : d.status === 'below' ? 'var(--yel)' : 'var(--grn)';
          html += '<tr style="border-top:1px solid var(--bd)"><td style="padding:3px 0">' + d.category + '</td><td style="text-align:right">' + d.currentPct + '%</td><td style="text-align:right;color:' + color + '">' + (d.driftPP > 0 ? '+' : '') + d.driftPP + 'pp</td></tr>';
        });
        html += '</table>';
      }
      if (r.topLevel) {
        html += '<div style="font-size:11px;color:var(--t3)">Ret: $' + (r.topLevel.retTotal || 0).toLocaleString() + ' | SAP: ' + (r.topLevel.sapUnits || 0) + 'u | Crypto: ' + (r.topLevel.cryptoUnits || 0) + 'u</div>';
      }
      if (r.notes) html += '<div style="font-size:12px;color:var(--t2);margin-top:6px;padding:6px 10px;background:var(--bg0);border-radius:6px;font-style:italic">"' + r.notes + '"</div>';
      html += '</div>';
    });
  }
  body.innerHTML = html;
  document.getElementById('rebalApproveBtn').style.display = 'none';
  document.getElementById('moRebal').classList.add('active');
};

// ─── CSS ───
import './styles/main.css';
// Neon skin imported AFTER main.css so it wins the cascade (root cause of olive/wash bleed:
// main.css is JS-injected after the index.html <link>, overriding the neon layer).
import './styles/spektrx-neon.css';
// Zen Spectrum skin, the default. Every rule inside is scoped to html[data-skin="zen"]; with the
// attribute absent only the Neon sheets paint (Settings → Appearance → Neon). Imported last so it
// wins over Neon.
import './styles/spektrx-zen.css';
import { initSkin, setSkin } from './ui/skin.js';

// index.html carries data-skin="zen" statically and public/skin-boot.js corrects it from storage
// before first paint (the CSP forbids an inline script, hence a file). This call is the fallback:
// if the boot script did not run (404, offline and uncached), a Neon device shows zen frames until
// here; the auth overlay covers them.
initSkin();


// =====================================================
// 1. SET SHARED STATE REFERENCES
// =====================================================
// Core modules need access to S but can't import state.js
// (circular dependency). They receive S via setter functions.
_setStateRef(S);
_setMathState(S);
_setPriceState(S);
_setLedgerStateRef(S);


// =====================================================
// 2. REGISTER CROSS-MODULE CALLBACKS
// =====================================================

// After successful login → load data, render, start prices
/**
 * One start-up sequence, used by both doors into the app.
 *
 * There were two: `_setOnLoginSuccess` (fresh login) and the `hasSession` branch of the init
 * IIFE (every ordinary reload). They were written separately and drifted, so six capabilities
 * ran or did not depending on which door you came in through:
 *
 *   login only    the 3-hourly MongoDB signal pull, _updateBellBadge, setupSWMessageListener,
 *                 setupNotifClickOutside, setSentryUser + addBreadcrumb
 *   restore only  backfillSalaryCashTxs
 *
 * The signal pull is how this was found. Tools -> Integrity reported the feed 4 days stale
 * while the settings were correctly configured, because the timer only ever started after a
 * fresh login — and a session that persists means you almost never log in again. A feature
 * documented as "every 3h while the app is open" had not run since the last time he typed a
 * password. Sentry is the quiet twin: errors from a restored session arrived with no user.
 *
 * Anything genuinely login-specific stays at the call site. Everything here runs both ways,
 * by construction. (audit 2026-08-03)
 */
async function startApp(opts = {}) {
  const fromLogin = !!opts.fromLogin;
  hideAuth();
  if (_sbUser) setSentryUser(_sbUser.id, _sbUser.email);
  addBreadcrumb('auth', fromLogin ? 'Login success' : 'Session restored');

  await load();
  setHydrated();   // nothing renders before this — see render-loop's hydration gate

  // Auto-reconcile: detect and repair missing cashTxs for trades/exits
  try {
    const audit = auditDataIntegrity();
    if (audit.issues.length) {
      console.warn('[ZAGX] audit: %d issue(s) — critical=%d warning=%d info=%d',
        audit.summary.total, audit.summary.critical, audit.summary.warning, audit.summary.info);
      audit.issues.forEach(i => console.warn('[ZAGX] issue:', i.severity, i.type, '—', i.msg));
    }
    if (!audit.ok) {
      // ⚠ `autoOnly` — the boot path applies only repairs that move NO money. On 2026-08-17 this
      // call had no gate, one detector went wrong, and eight page loads invented ₺6,542,158.72
      // under a green toast. A money-moving repair is now REPORTED and waits for a click in
      // Settings. See the block at the top of reconcile.js section 2.
      const result = reconcileAndRepair({ autoOnly: true });
      if (result.heldBack > 0) {
        console.warn(`[ZAGX] auto-repair: ${result.heldBack} money-moving repair(s) HELD BACK — review in Settings`);
        toast(`${result.heldBack} cash issue(s) need review — Settings → Data integrity`, 'a-y');
      }
      if (result.failed > 0) {
        console.warn(`[ZAGX] auto-repair: ${result.repaired} fixed, ${result.failed} FAILED`);
        toast(`Reconcile: ${result.repaired} fixed, ${result.failed} FAILED — see console`, 'a-r');
      } else if (result.repaired > 0) {
        console.warn(`[ZAGX] auto-repaired ${result.repaired} issue(s)`);
        toast(`Data reconciled: ${result.repaired} issue(s) fixed`, 'a-g');
      }
      if (result.repaired > 0) {
        try { await cloudSave(); } catch (e) { console.error('[ZAGX] post-reconcile cloud save failed:', e); }
      }
    }
  } catch (e) { console.error('[ZAGX] reconcile error:', e); }

  // Idempotent backfill — ran only on the restore path before.
  try { backfillSalaryCashTxs(); } catch (e) { console.error('[ZAGX] salary backfill error:', e); }

  renderAll();
  updateSyncBadge(_isOnline ? 'cloud' : 'offline', _isOnline ? 'synced' : 'offline');
  if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission();

  // Ran only on the login path before: push plumbing and the bell count.
  setupSWMessageListener();
  setupNotifClickOutside();
  _updateBellBadge();

  setTimeout(() => { try { scanMaturities(); } catch (_e) {} }, 1500);
  await refreshPrices();
  setupPoll();

  // Auto-pull MongoDB signals every 3h while the app is open. Dedupe makes repeats no-ops.
  if (window._mongoSigTimer) clearInterval(window._mongoSigTimer);
  const _mongoPull = () => {
    if (!(S.settings?.mongoDb && S.settings?.mongoColl)) {
      console.warn('[ZAGX] signal auto-pull skipped — mongoDb/mongoColl not set in Settings');
      return;
    }
    try { fetchSignalsFromMongo().catch(e => console.warn('[ZAGX] signal pull failed:', (e && e.message) || e)); }
    catch (e) { console.warn('[ZAGX] signal pull threw:', (e && e.message) || e); }
  };
  setTimeout(_mongoPull, 60000);                                        // first pull ~1 min in
  window._mongoSigTimer = setInterval(_mongoPull, 3 * 60 * 60 * 1000);  // then every 3 hours

  // Populate settings form
  document.getElementById('sBase').value = S.base;
  document.getElementById('sTdKey').value = S.settings.tdApiKey || '';
  document.getElementById('sEq').value = S.settings.eqInt;
  document.getElementById('sCr').value = S.settings.crInt;
  document.getElementById('sNot').value = S.settings.notif ? '1' : '0';
  document.getElementById('sBkDays').value = S.settings.backupDays || 7;
  document.getElementById('sRebalTh').value = S.settings.rebalThreshold || 5;
  document.getElementById('sInfl').value = ((S.settings.trInflation || 0) * 100).toFixed(1);
  checkBackup();
  updateBackupInfo();
}

_setOnLoginSuccess(() => startApp({ fromLogin: true }));

// After persistence completes (idb/cloud load)
_setOnLoadComplete(() => {
  // Belt and braces: load() calls this at the end of its own body, so if any path returns
  // through here rather than through the awaits below, the gate still opens.
  setHydrated();
  renderAll();
  _updateBellBadge();
});

// After prices refresh → re-render relevant sections
_setOnPricesRefreshed(() => {
  // Update signal statuses based on new prices (before render, no save inside render)
  try { updateSignalStatuses(); } catch (_e) { /* signals optional */ }
  // Real-time SL/TP proximity alerts (no LLM, client-side only)
  try { generateQuickAlerts(); } catch (_e) { /* copilot alerts optional */ }
  // Scenario playbook evaluation (client-side conditional rules)
  try { evaluateScenarios(); } catch (_e) { /* scenario eval optional */ }
  markDirty('dash', 'port', 'trades', 'goals', 'signals');
  renderAll();
  // chkAlerts() removed — scanTradeCrossings (prices.js, crossing-based + deduped) is the
  // authoritative SL/TP alert scanner; chkAlerts re-fired every tick = duplicate/spam toasts. audit M (dup alerts).
});

// CRITICAL: Wire persistence functions into state.js so save() writes to IDB + Supabase
// Without this, save() only writes to LS — cloud sync is completely broken.
_setPersistenceFunctions(idbSaveState, cloudSave, cloudSnapshot, canWriteLocalState);
// STABILIZATION: Wire renderAll + lg into state.js for resolveConflict (avoids circular imports)
_setRenderCallbacks(renderAll, lg);

// Wire online status callback
_setOnOnline((online) => {
  updateSyncBadge(online ? 'cloud' : 'offline', online ? 'synced' : 'offline');
});


// =====================================================
// 3. WRAPPER FUNCTIONS (complex inline handlers)
// =====================================================
const confirmResetAll = () => { resetAll(); }; // resetAll() shows its own neon confirm
const triggerImport = () => { document.getElementById('impF').click(); };
const fetchFxAndRefresh = async () => {
  await fetchFx(); initSett(); renderAll(); toast('FX refreshed', 'a-b');
};
const saveCFMoAndClose = () => { saveCFMo(); clMo('moCF'); };
const closeMoAndRender = () => { clMo('moM'); renderAll(); };
const reloadPage = () => { window.location.reload(); };
const toggleAIPanel = () => {
  const grid = document.getElementById('contentGrid');
  const toggle = document.getElementById('panelToggle');
  if (!grid) return;
  const collapsed = grid.classList.toggle('panel-collapsed');
  if (toggle) toggle.textContent = collapsed ? '▶' : '◀';
  try { localStorage.setItem('spektrx-panel', collapsed ? 'collapsed' : 'open'); } catch (_e) { /* ignore */ }
};
const skinChange = (e) => { setSkin(e && e.target ? e.target.value : ''); };
const toggleTheme = () => {
  const html = document.documentElement;
  const current = html.getAttribute('data-theme');
  const next = current === 'light' ? 'dark' : 'light';
  html.setAttribute('data-theme', next === 'dark' ? '' : next);
  if (next === 'dark') html.removeAttribute('data-theme');
  try { localStorage.setItem('spektrx-theme', next); } catch (_e) { /* ignore */ }
};
const dismissBanner = (_e) => {
  const banner = document.getElementById('swUpdateBanner');
  if (banner) banner.remove();
};
const navTo = (page) => {
  // Use grouped nav activator — finds the right group and switches to sub-page
  if (window._navActivateTab) {
    window._navActivateTab(page);
  } else {
    // Fallback: direct tab click
    const tab = document.querySelector(`[data-page=${page}]`);
    if (tab) tab.click();
  }
};
const touchAndCalcTd = (e) => {
  e.target.dataset.touched = '1';
  calcTdTradePreview();
};

// =====================================================
// 4. ACTION MAP + EVENT DELEGATION (CSP-compliant)
// =====================================================
// All functions accessible via data-action, data-action-input, data-action-change
const _actions = {
  // Auth
  authLogin, authLogout,
  // Trades Journal subpage
  openJournal, jrnFilter, jrnRowToggle, renderJournal,
  openTrades: () => { if (window._navActivateTab) window._navActivateTab('trades'); },
  // Tax Module (viewer)
  renderTax, taxPickFile, taxImportFile, taxSelectYear, taxToggleWarn, taxMarkSent, taxBookRefund,
  renderIntegrity, toggleIntegrityRow,
  // State
  resolveConflict,
  // Layout
  clMo, toast, fixMobileLayout, toggleMobileMore,
  // Render
  renderAll, renderOnly, markDirty,
  // Portfolio
  openAddH, editH, saveH, delH, openBulk, saveBP, srt,
  manP, inlinePrice, closeDailyTd, confirmMaturity, changeTdRate,
  calcPortfolioXIRR, calcHoldingXIRR, renderP,
  switchPortfolio, openPortfolio, savePortfolio, closePortfolio,
  closeEditingPortfolio, deletePortfolio,
  renderPortfolioBar, renderPortfolioList,
  openExitH, clMoExitH, execExitH, exitAvgAll, openAddLot, clMoAddLot, saveAddLot,
  openLots, clMoLots, delLot,
  // Monthly
  openAddMo, saveMo, delMo, reconcileMo,
  exportMoCsvTemplate, importMoCsv,
  saveRow, skipRow, calcPlan, populateMonthSelect, renderM,
  populateMoPortfolio, moPortChanged,
  // Trades
  openAddT, editTr, saveT, openExit, execExit, openAddPosition, execAddPosition,
  scanMaturities, scanTradeCrossings,
  toggleTdTradeFields, onTdKindChange,
  calcTdTradePreview, calcExpPnl,
  // PROC-003: an unregistered data-action fires an ANCESTOR's action, silently.
  thxToggle, thxPreview,
  inlineTPrice, manTP, notify,
  renderTradeJournal,
  trFoldToggle, trQuietToggle,
  dashNeedToggle,
  // Cash
  openCTx, saveCTx, onCashReasonChange, delCashTx, cTxToggleAll, setCashTxClass, reconcileCashTRY, openFxConvert, calcFxConvert, saveFxConvert, rfAction, rfEditTx, rfDeleteTx,
  renderC, renderPnlCh, renderRF, mnyToggle, stmOpen,
  // Reconciliation
  auditDataIntegrity, reconcileAndRepair,
  // History
  lg, renderLog, setLogTF,
  // Alpha Ledger (Decision Loop)
  openLedger, renderLedgerPage, advSelfActed, advWhyDisCancel,
  // Dashboard
  renderDash, toggleDashSection, setTimelineRange, setHeatmapMode, _dashSnapshot, backfillBenchmark, backfillFxRates,
  // Cashflow
  openCFMonth, saveCFMo, closeCFMonth, cfDeleteMonth,
  cfAddIncome, cfRemoveIncome, cfSetIncome,
  cfSetAmt, cfSetField, cfPaidByChanged, cfOneOffChanged, cfToggle,
  cfMoChanged, cfIncomeFooter, cfCalcSummary,
  renderCF, renderProjection, renderCFModal,
  openExpDef, saveExpDef, deleteExpDef, renderExpDefs,
  renderCFHist, toggleEdLoan,
  openOTI, saveOTI, deleteOTI, renderOTIList,
  // Settings
  testTdKey, testBistProxy, saveFxRates, saveSett, initSett,
  requestNotifPerm, updateNotifPermUI, renderTaxSummary,
  loadBackupList, restoreBackup,
  filterQP, renderQP, saveQP, toggleQPPanel,
  filterQPF, renderQPF, saveQPF,
  resetAll, exportAll, importData, exportCSV, toggleBistHelp,
  checkBackup, hideBackupBanner, updateBackupInfo,
  openPBR, confirmBackup, skipBackup, doBackup,
  daysSinceBackup,
  // Prices
  refreshPrices, fetchFx, setupPoll,
  // Debt
  runDebtPayoff, dpMoveUp, dpMoveDown, getActiveLoans,
  // Budget
  openBudgetSettings, saveBudgetSettings, renderBudget,
  // Dividends
  holdingDividends, openDivModal, openDivModalStandalone, calcDvNet, dvTickerLookup, saveDividend, renderDividendList, deleteDividend,
  // What-If
  resetWhatIf, populateWIExpenses, wiCalcProjection, runWhatIf,
  // Liabilities
  openLiability, saveLiability, deleteLiability, clMoLiab,
  openLiabPay, execLiabPay, clMoLiabPay, deletePayment,
  // Goals
  calcFILevel, checkMilestones, openGoal, saveGoal, deleteGoal, clMoGoal,
  saveGoalSettings, toggleGoalLink, renderGoals, renderGoalsDashCard,
  // Signals
  renderSig, openPasteSignal, execPasteSignal, deleteSignal, expireSignal, saveTickerMapping, updateSignalStatuses, setSignalFilter, dismissSignalAlert, dismissAllSignalAlerts, browseSignalFile, openTradeFromSignal, inlineTickerSave, reResolveAllSignals, fetchSignalsFromMongo, enrichSignalsAI, applySignalTP,
  // The Signals filter bar. `signalFilterChange` reads `data-field`; see signals.js.
  signalFilterChange, clearSignalFilters,
  // Bonus
  renderBonus, openBonusQuarter, saveBonusQuarter, deleteBonusQuarter, markBonusPaid, switchBonusPlan, openBonusPlan, saveBonusPlan, deleteBonusPlan, allocateBonus, saveAllocation, bonusScanProfile, bonusScanLens, bonusScanAct, bonusRescan, bonusScanAllocate, bonusScanLockAll,
  // Search
  openSearch, closeSearch,
  // AI Categorization
  openAICat, closeAICat, aiCatToggle, aiCatChange, aiCatChangeEvt, aiCatApproveAll, aiCatSave, aiCatManual,
  // AI Portfolio Insights
  requestAIInsights, dismissAIInsight, clearAIInsights,
  // AI Digest
  generateDigest,
  // AI Trade Journal
  aiReviewTrade, addJournalEntry, saveJournalEntry, cancelJournalEdit, deleteJournalEntry, toggleJournalExpand,
  // Push Notifications
  toggleNotifCenter, closeNotifCenter, setNotifFilter, markNotifRead, markAllNotifsRead, dismissNotif, clearAllNotifs, savePushSettings, testPushNotif,
  // AI Chat + Actions
  openAIChat, closeAIChat, toggleAIChat, sendChatMessage, clearChatHistory, selectQuickAction, aiActionConfirm, aiActionCancel,
  // AI Copilot (P4)
  runCopilotScan, cancelCopilotScan, dismissRecommendation, dismissAlert, clearCopilotData,
  // AI Advisory page
  advLens, advToggle, advSeeOrig, advDismiss, advAct, advScan,
  activatePreset, toggleScenario, deleteScenario, runDeepScan, scanDCAOpportunities,
  // Rebalancing
  startRebalanceReview, approveRebalanceReview, showRebalHistory, completeReview, completeAction,
  // AI Settings
  aiProviderChange, saveAISettings, testAI,
  // Charts
  renderAllocCh,
  // Cashflow — item actions
  cfAddItem, cfRemoveItem,
  // Wrappers (complex handlers)
  confirmResetAll, triggerImport, fetchFxAndRefresh,
  saveCFMoAndClose, closeMoAndRender, reloadPage,
  dismissBanner, touchAndCalcTd, navTo, toggleTheme, toggleAIPanel,
  skinChange,
};

// Register event delegation (click + input + change)
setupEventDelegation(_actions);

// ── GLOBAL EXPOSURE ──
// Modules use window.X for cross-module calls that would create circular imports.
// This is intentional architecture — NOT a bug. All functions listed here are
// required by one or more modules at runtime via implicit global access.
Object.assign(window, _actions);
Object.assign(window, {
  // Utils & state
  esc, f, fU, fT, fP, pc, toUSD, fByCur, uid, now, ds, ml, VALID_CURRENCIES,
  S, save, stampItem,
  // Persistence
  load, cloudSave, cloudLoad, cloudSnapshot,
  idbSave, idbList, idbRestore, idbClear,
  // Math
  xirr, hVal, hPnl, mTRY, getMs, hMoTRY, hTotTRY, hTotUSD, tdAccrued,
  // Render engine
  renderAll, markDirty, renderOnly,
  // Module renders (cross-referenced by other modules)
  renderP, renderC, renderT, renderDash, renderM, renderCF, renderLog, renderGoals, renderSig, renderRisk, renderBonus, setSignalFilter, updateSignalStatuses, dismissSignalAlert, dismissAllSignalAlerts, openTradeFromSignal, inlineTickerSave, reResolveAllSignals,
  renderBudget, renderRF, renderPnlCh, renderGoalsDashCard,
  // Portfolio management
  switchPortfolio, openPortfolio, savePortfolio, closePortfolio, deletePortfolio,
  getDefaultPortfolio, getActivePortfolio, getPortfolioHoldings, populatePortfolioSelect,
  openExitH, clMoExitH, execExitH, exitAvgAll, openAddLot, clMoAddLot, saveAddLot,
  openLots, clMoLots, delLot,
  // AI Copilot getters (consumed by UI, not event-delegated)
  getCopilotRecommendations, getCopilotAlerts, getCopilotStatus, isCopilotScanning,
  getScenarios, getPresetStatus, SCENARIO_PRESETS, createScenario,
  // Module functions used cross-module
  lg, holdingDividends, scanTradeCrossings, scanMaturities,
  projectMonthlyIncome, resetWhatIf, populateWIExpenses, runDebtPayoff,
  renderLiabilities, totalLiabilitiesUSD, totalLiabilitiesTRY,
  // Auth state (needed by settings.js resetAll)
  _sb, _sbUser, _sbReady, _isOnline, _accessToken, SB_REST, _sbHeaders,
  // Error monitoring
  captureError, addBreadcrumb,
  // AI categorization helpers (used by cash.js render)
  catBadge, getCatStats,
  // AI insights (used by dashboard.js via window global)
  renderCachedInsights,
  // AI trade journal helpers (used by trades.js render)
  getJournalBadge, getJournalExpandRow, offerPostExitReview, refreshExpandedJournals,
  // Push notifications (used by trades.js + signals.js via window.swNotify, settings.js via window.initPushSettings)
  swNotify, getUnreadCount, _updateBellBadge, renderNotifCenter, initPushSettings,
});


// =====================================================
// 5. BEFOREUNLOAD — flush pending cloud saves
// SYNC-03 Fix: Flush debounced cloudSave before tab close
// =====================================================
/** Browsers cap all keepalive bodies at 64KB per document; leave headroom for headers. */
const KEEPALIVE_MAX_BYTES = 60 * 1024;

window.addEventListener('beforeunload', () => {
  // 1. Flush any pending state to localStorage (synchronous, always works)
  try {
    if (canWriteLocalState()) localStorage.setItem('zagx_v3', JSON.stringify(S));
  } catch (_e) {}

  // 2. If there's a pending debounced cloudSave, fire it immediately
  if (_getSaveTimer()) {
    _clearSaveTimer();
    // Fire cloudSave synchronously via sendBeacon (best-effort, no auth headers)
    // Since sendBeacon can't include Authorization headers for Supabase,
    // we use a fire-and-forget fetch instead (browser may or may not complete it)
    try {
      // Use keepalive flag — browser will try to complete this even after tab closes
      const token = _accessToken;
      if (token && _sbUser && _isOnline) {
        // Strip transient data (same as cloudSave in persistence.js)
        const { prices: _prices, history: _history, snapshots: _snapshots, notifications: _notifications, _fxTs, _saveCount, alertedKeys: _alertedKeys, ...persistentState } = S;
        persistentState.snapshots = syncableSnapshots(S);

        // ⚠ SYNC-003 — THE FOURTH SERIALISER, AND THE ONE `secrets.js` NEVER REACHED.
        //
        // `core/secrets.js` exists because "never sync secrets" had been copied into two of the
        // three serialisers and missed at the third — `doBackup`, which wrote the whole state to
        // a file that reached a repository **twenty-six times with two live API keys in it**.
        // The remedy was one list and three callers. **This is the fourth caller, and it was not
        // one of them.** Same file, same rule, the next copy.
        //
        // Measured 2026-08-15: this payload is **315 KB against a 64 KB keepalive budget**, so
        // the guard below discards it and the key does not travel — today. The trigger is the
        // payload dropping under 64 KB: `signals` alone is 155 KB of it and is capped and
        // pruned, and **a new device with a fresh account starts well under the limit**, so
        // there the very first tab close would POST the key.
        //
        // `hasSecrets` asks the same question the other two serialisers now ask (SEC-001). If it
        // ever answers yes, a new secret path reached the state without being added to
        // SECRET_PATHS, and the right answer is to send nothing.
        const safeState = stripSecrets(persistentState).state;
        if (hasSecrets(safeState)) {
          console.error('[ZAGX] unload keepalive refused: payload still carries a secret path');
          return;
        }
        const body = JSON.stringify({ user_id: _sbUser.id, state: safeState });
        // A keepalive request is capped at 64KB across the whole document. This app's state
        // is around 290KB, so for years this call looked like a safety net and was in fact
        // discarded by the browser before it left. Send it only when it can actually go, and
        // say so when it cannot, rather than firing into a limit we already know about.
        if (body.length > KEEPALIVE_MAX_BYTES) {
          console.warn('[ZAGX] unload keepalive skipped — ' + Math.round(body.length / 1024)
            + 'KB exceeds the 64KB keepalive budget. The tab-hide flush and localStorage are the real path.');
          stopCloudPoll();
          return;
        }
        fetch(SB_REST + '/user_state?on_conflict=user_id', {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': 'Bearer ' + token,
            'Content-Type': 'application/json',
            'Prefer': 'resolution=merge-duplicates,return=minimal'
          },
          body,
          keepalive: true  // Critical: allows request to outlive the page
        }).catch(() => {}); // Swallow network errors on page unload — nothing to do
      }
    } catch (_e) {
      // Best-effort — LS is the safety net, cloud syncs on next load
    }
  }

  // 3. Stop polling (cleanup)
  stopCloudPoll();
});

// 5b. VISIBILITYCHANGE — primary reliable cloud flush
// The beforeunload keepalive fetch silently fails when the state payload exceeds
// the browser's 64KB keepalive limit (this app's payload is ~290KB), which let the
// cloud copy drift behind local. Firing a full cloudSave the moment the tab is
// hidden runs while the page is still alive, so the request completes normally on
// tab switch / minimize / close-start — closing the drift at the source.
//
// The flush-then-rearm body used to live here, and a second caller needed it (DATA-011: a
// restore has to reach the cloud before anything can merge against it). It now lives in
// `state.js:flushCloudNow`, next to the timer it manipulates and reachable by both. cloudSave
// answers false without writing when offline, session-less, circuit-broken, already in flight,
// or blocked by the load-failed guard — and the beforeunload keepalive is itself gated on the
// timer, so a bare clear disarms the documented fallback too. The re-arm is inside the function.
function _flushOnLeave(reason) {
  flushCloudNow(reason);
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') _flushOnLeave('hide');
});

// pagehide is the one that fires on iOS Safari and on a bfcache freeze, where beforeunload
// does not. Same flush; persisted=true means the page may come back, so this is not a close.
window.addEventListener('pagehide', (e) => {
  try { if (canWriteLocalState()) localStorage.setItem('zagx_v3', JSON.stringify(S)); } catch (_e) { /* quota */ }
  _flushOnLeave(e.persisted ? 'bfcache' : 'pagehide');
});

// =====================================================
// 6. DOM READY SETUP
// =====================================================
document.addEventListener('DOMContentLoaded', () => {
  // Light theme is not yet supported by the neon skin — force dark and clear any stale pref
  // so previously-toggled users aren't left with a broken mixed UI. (audit C-1)
  try {
    document.documentElement.removeAttribute('data-theme');
    if (localStorage.getItem('spektrx-theme') === 'light') localStorage.removeItem('spektrx-theme');
    // The Appearance control is in the DOM only now; bring it in line with the skin already applied.
    initSkin();
    // Restore AI panel state
    const panelState = localStorage.getItem('spektrx-panel');
    if (panelState === 'collapsed') {
      const grid = document.getElementById('contentGrid');
      const toggle = document.getElementById('panelToggle');
      if (grid) grid.classList.add('panel-collapsed');
      if (toggle) toggle.textContent = '▶';
    }
  } catch (_e) { /* ignore */ }
  // Setup navigation tabs with dirty-flag system
  const _pageRenderMap = {
    dashboard: renderDash, portfolio: renderP, monthly: renderM,
    trades: renderT, cash: renderC, cashflow: renderCF, goals: renderGoals,
    risk: renderRisk, advisory: renderAdvisory,
    bonus: renderBonus, history: renderLog, initSett: initSett,
    journal: renderJournal, tax: renderTax, integrity: renderIntegrity
  };
  setupNavigation(_pageToSection, _pageRenderMap, _dirty);
  // AI Advisory P6: app-open slot scans (08:30/12:30 weekdays, 18:00 daily TR time)
  initAIOrchestrator();
  // Decision Loop capture layer: append-only advisory event ledger (own IDB + Supabase table)
  initDecisionLedger();
  // Tax Module: load imported engine snapshots from IDB (local-only, never cloud)
  initTax();
  // Statements (Income & Expenses): the statement engine's per-card summaries, same viewer contract
  initStatements();
  // Setup unified modal behavior (Esc + backdrop click)
  setupModals();
  // Setup keyboard navigation (1-8 pages, / search, ? help)
  setupKeyboard();
  // Setup scroll fade indicators on tables
  setupScrollFades();
  // Global back-to-top button
  setupToTop();
  // Fix mobile layout immediately
  fixMobileLayout(true);
  // Neon aurora ambient background (full-spectrum on dashboard shell)
  // Background is now a deterministic CSS gradient (matches prototype) — no animated canvas overlay.
});

// Resize handler
window.addEventListener('resize', () => fixMobileLayout(true));


// =====================================================
// 5. APP INIT (Supabase-first)
// =====================================================
(async function () {
  // 0. Fix mobile layout immediately (before auth/render)
  fixMobileLayout();

  // Cross-page LIVE wealth calcs (Portfolio hero Net Growth, Trades hero risk ring).
  // Wired here like other window.* globals — avoids portfolio→dashboard import cycle
  // (dashboard imports calcPortfolioXIRR from portfolio). Always-fresh, unlike __twUSD.
  window.__computeTW = computeTotalWealthUSD;
  window.__netGrowth = netGrowthUSD;

  // Version/build stamp — confirm at a glance which build is live (footer + global + console).
  try {
    const _ver = (typeof __APP_VERSION__ !== 'undefined') ? __APP_VERSION__ : '?';
    const _bld = (typeof __BUILD_TIMESTAMP__ !== 'undefined') ? __BUILD_TIMESTAMP__ : '?';
    // ⚠ THE COMMIT, NOT JUST THE BUILD. A timestamp says a build happened; it cannot say WHICH
    // commit is live. On 2026-08-17 a hotfix push did not trigger a Netlify build, nobody could
    // tell from the app, and four more loads of a money pump ran — ₺3.27 m. PROC-011/PROC-015.
    const _sha = (typeof __COMMIT_SHA__ !== 'undefined') ? __COMMIT_SHA__ : 'unknown';
    window.__SPEKTRX_BUILD__ = { version: _ver, build: _bld, sha: _sha, short: String(_sha).slice(0, 7) };
    console.warn('[SPEKTRX] v' + _ver + ' · build ' + _bld + ' · commit ' + String(_sha).slice(0, 7)); // warn survives prod console gating
    if (!document.getElementById('spkVer')) {
      const vs = document.createElement('div');
      vs.id = 'spkVer';
      vs.textContent = 'v' + _ver + ' · ' + _bld + ' · ' + String(_sha).slice(0, 7);
      vs.style.cssText = 'position:fixed;bottom:6px;left:8px;z-index:50;font:10px/1 ui-monospace,monospace;color:rgba(159,176,208,.45);pointer-events:none;letter-spacing:.04em';
      document.body.appendChild(vs);
    }
  } catch (_e) { /* version stamp non-critical */ }

  // 1. Register service worker + auto-update mechanism (PROD only — in dev the SW would cache the
  //    dev bundle and serve stale code, the exact "two versions at one URL" trap)
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    try {
      // Pre-check: ensure /sw.js returns JS, not Cloudflare Access HTML redirect
      const swCheck = await fetch('/sw.js', { method: 'HEAD' }).catch(() => null);
      const swType = swCheck?.headers?.get('content-type') || '';
      if (!swCheck || !swCheck.ok || (swType && !swType.includes('javascript'))) {
        console.warn('[PWA] sw.js not reachable or not JS (type: ' + swType + '), skipping SW');
        throw new Error('SW file not reachable');
      }
      // ⚠ CAPTURED BEFORE register(), AND BEFORE BOTH LISTENERS BELOW.
      //
      // A page that is already controlled was controlled at load; a page that is not is seeing
      // its FIRST worker. That distinction is the whole difference between an update and an
      // install, and until 2026-08-15 it did not matter, because the worker never activated
      // (SEC-004). Turning it on turned on everything downstream of it — and there are TWO
      // consumers, not one. The first fix guarded only the reload, shipped, and the banner
      // announced "New version available" for the version already on screen. Measured on his
      // machine, one deploy later. Rule #14: do not stop at the one instance you were handed.
      //
      // `clients.claim()` in the worker's activate can set `navigator.serviceWorker.controller`
      // before `statechange` fires, so reading it inside the listener answers a different
      // question than the one being asked. Read it here, once.
      const _hadController = !!navigator.serviceWorker.controller;

      const reg = await navigator.serviceWorker.register('/sw.js');
      // Force browser to check for new SW immediately
      if (reg) reg.update().catch(() => {});

      // When a new SW is found and installed, show update toast
      let _pendingUpdate = false;
      reg.addEventListener('updatefound', () => {
        const newSW = reg.installing;
        if (newSW) {
          newSW.addEventListener('statechange', () => {
            if (newSW.state === 'activated' && navigator.serviceWorker.controller && _hadController) {
              console.log('[PWA] New version available');
              _pendingUpdate = true;
              const banner = document.createElement('div');
              banner.id = 'swUpdateBanner';
              banner.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:9999;background:linear-gradient(135deg,#8b5cf6,#a78bfa);color:#fff;padding:10px 20px;display:flex;align-items:center;justify-content:space-between;font-size:13px;font-weight:500;box-shadow:0 2px 12px rgba(123,63,228,.3)';
              banner.innerHTML = '<span>New version available</span><div><button data-action="reloadPage" style="background:#fff;color:#8b5cf6;border:none;padding:5px 14px;border-radius:6px;cursor:pointer;font-weight:600;margin-right:8px">Reload Now</button><button data-action="dismissBanner" style="background:transparent;color:#fff;border:1px solid rgba(255,255,255,.4);padding:5px 10px;border-radius:6px;cursor:pointer">Later</button></div>';
              document.body.prepend(banner);
            }
          });
        }
      });

      // controllerchange — only reload if user already dismissed/accepted or on fresh load.
      // Second consumer of `_hadController`, declared above: on a first install there was no
      // previous controller to change away from, so a reload here would restart the boot for
      // the version already running.
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing && !_pendingUpdate && _hadController) {
          refreshing = true;
          console.log('[PWA] Controller changed (no pending UI), reloading...');
          window.location.reload();
        }
      });

      console.log('[PWA] Service worker registered, update check triggered');
    } catch (e) { console.warn('[PWA] SW registration failed:', e); }
  } else if (!import.meta.env.PROD && 'serviceWorker' in navigator) {
    // Dev: unregister any stale SW so the Vite dev server always reflects the latest source
    navigator.serviceWorker.getRegistrations().then(rs => rs.forEach(r => r.unregister())).catch(() => {});
  }

  // 2. Init Supabase
  sbInit();

  // 3. Check existing session (auto-login if session persists)
  const hasSession = await authCheckSession();
  if (hasSession) {
    await startApp({ fromLogin: false });
  } else {
    // No session — show login screen
    showAuth();
  }
})();

console.log('[ZAGX] v2.0 — Modular build loaded');
